import { Archive } from "@material-ui/icons";
import {
  CreateView,
  defaultRowButtonsCreator,
  IndexView,
  Schema,
  ShowView
} from "lego-gui-lib";
import { get } from "lodash";
import { inject } from "mobx-react";
import { observer } from "mobx-react-lite";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { RowButton } from "../../../common/RowButtons";
import { languageContext } from "../../language/schema";
import { MediaType, messageContext } from "../schema";
import {
  AnnouncementFormSchema,
  AnnouncementIndexSchema,
  messageDataContext,
  SMSDataSchema,
  SMSFormSchema
} from "./schema";
import { useHistory } from "react-router";

const injectDataSource = (comp: any) =>
  inject(
    "messageData",
    "message",
    "language",
    "i18n",
    "commons"
  )(observer(comp));

export const MessageDataCreate = injectDataSource(
  ({ messageData, language, message, match, i18n, commons, ...rest }: any) => {
    const title = i18n.t("common:create", {
      domain: "hns:messageData:domain"
    });

    const mediaType = get(message.oneDataSource, "one.mediaType");
    let schema: Schema;
    let submit: (() => void) | undefined;
    if (mediaType !== undefined && mediaType === MediaType.SMS) {
      schema = SMSFormSchema;
      submit = undefined;
    } else {
      schema = AnnouncementFormSchema;
      submit = () =>
        messageData.oneDataSource.upload().then((response: any) => {
          commons.newSuccess(i18n.t("hns:message:data.uploaded"));
        });
    }
    const history = useHistory();

    return schema ? (
      <CreateView
        dataSource={messageData.oneDataSource}
        title={title}
        {...rest}
        fields={schema}
        submit={submit}
        goBack={() => history.goBack()}
      />
    ) : null;
  }
);

export const MessageDataEdit = injectDataSource(
  ({ messageData, message, match, i18n, ...rest }: any) => {
    const title = i18n.t("common:edit", {
      domain: "hns:messageData:domain"
    });
    const history = useHistory();

    const mediaType = get(message.oneDataSource, "one.mediaType");
    const canEdit = mediaType && mediaType === MediaType.SMS;

    return canEdit ? (
      <ShowView
        id={{ id: match.params.dataId }}
        dataSource={messageData.oneDataSource}
        title={title}
        {...rest}
        goBack={() => history.goBack()}
        fields={SMSFormSchema}
      />
    ) : null;
  }
);

const rowButtons = (canEditSMSOnly: boolean, isAllowed: boolean) => {
  const defaultButtonsConfig = isAllowed
    ? { edit: canEditSMSOnly, delete: true, copy: false, export: false }
    : { edit: false, delete: false, copy: false, export: false };

  return (ctrl: any) => (row: any, key: string, close: () => void) => {
    const buttonsCreator = defaultRowButtonsCreator(defaultButtonsConfig)(ctrl);
    const download = () => {
      ctrl.props.dataSource.downloadData(row);
      close();
    };
    const buttons = buttonsCreator(row, key, close);

    if (!canEditSMSOnly) {
      buttons.push(
        <RowButton
          callbackFunction={download}
          title={["common:download", "Download"]}
          key={"download"}
          requiredAccess={"readable"}
        >
          <Archive />
        </RowButton>
      );
    }

    return buttons;
  };
};

const toolbarButtons = (isAllowed: boolean) => {
  return {
    delete: isAllowed,
    export: false
  };
};

const titleButtons = (canAdd: boolean, isAllowed: boolean) => () => {
  if (isAllowed && canAdd) {
    return undefined;
  } else {
    return <></>;
  }
};

export const MessageDataIndex = observer((props: any) => {
  const i18n = useTranslation();
  const title = i18n.t("hns:messageData:domain_plural");

  const { isAllowed, ...rest } = props;
  const message = useContext(messageContext);
  const messageData = useContext(messageDataContext);
  const totalMessageData = messageData.listDataSource.total || 0;

  const mediaType = get(message.oneDataSource, "one.mediaType");
  const isSMS = mediaType === MediaType.SMS;

  const schema =
    mediaType !== undefined &&
    (mediaType === MediaType.SMS ? SMSDataSchema : AnnouncementIndexSchema);

  const language = useContext(languageContext);
  const totalLanguage = language.listDataSource.total || 0;
  const canAdd = totalLanguage > totalMessageData;

  return (
    <>
      {schema && (
        <IndexView
          {...rest}
          dataSource={messageData.listDataSource}
          titleButtons={titleButtons(canAdd, isAllowed)}
          toolbarButtons={toolbarButtons(isAllowed)}
          rowButtons={rowButtons(isSMS, isAllowed)}
          title={title}
          baseUrl="/data"
          columns={schema}
        />
      )}
    </>
  );
});
